import React from "react";
import {
    Box,
    Chip,
    Dialog,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CalendarMonthOutlined, CurrencyExchangeOutlined } from "@mui/icons-material";
import { useModalStyles } from "../../common/useModalStyles";
import { InvoiceDetailDTO, LateFeeHistoryDTO } from "../../../redux/slice/invoice/InvoiceSlice";
import Button from "../../common/Button";
import CancelIcon from "@mui/icons-material/Cancel";

interface LateFeeSummaryPopupProps {
    open: boolean;
    onClose: () => void;
    selectedInvoice?: InvoiceDetailDTO;
    lateFeeHistory?: LateFeeHistoryDTO[];
}

const modalContentPx = { xs: 1.5, sm: 2.8 } as const;

const LateFeeSummaryPopup: React.FC<LateFeeSummaryPopupProps> = ({
    open,
    onClose,
    selectedInvoice,
    lateFeeHistory = [],
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        cancelBtnSx,
    } = useModalStyles();

    const formatCurrency = (amount: number = 0) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        }).format(amount);

    const formatOverdueDays = (days?: number) => {
        if (days === null || days === undefined) return "-";
        const numericDays = Number(days);
        if (Number.isNaN(numericDays)) return "-";
        return `${numericDays < 0 ? "-" : ""}${Math.abs(numericDays)} Days`;
    };

    const formatDateTime = (value?: string) => {
        if (!value) return "-";
        const normalizedValue = value.includes(" ") && !value.includes("T")
            ? value.replace(" ", "T")
            : value;
        const date = new Date(normalizedValue);
        if (Number.isNaN(date.getTime())) return value;

        return new Intl.DateTimeFormat("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }).format(date);
    };

    const getTypeChipSx = (type?: string) => {
        if (type === "Scheduled") {
            return {
                color: tokens.accent,
                backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.12)",
                borderColor: `${tokens.accent}55`,
            };
        }

        if (type === "Manual") {
            return {
                color: "#f59e0b",
                backgroundColor: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)",
                borderColor: "rgba(245,158,11,0.35)",
            };
        }

        return {
            color: tokens.textSecondary,
            backgroundColor: isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.1)",
            borderColor: isDark ? "rgba(148,163,184,0.25)" : "rgba(148,163,184,0.35)",
        };
    };

    const tableHeaderBg = isDark ? "#1C2533" : "#F8FAFC";
    const tableHeaderBorder = isDark ? "rgba(255,255,255,0.05)" : "#EEF2F6";
    const tableRowBg = isDark ? "rgba(255,255,255,0.018)" : "#ffffff";
    const tableRowBorder = isDark ? "rgba(255,255,255,0.04)" : "#EEF2F6";
    const tableRowHover = isDark ? "rgba(255,255,255,0.04)" : "#F1F5F9";
    const tableEmptyBg = isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC";
    const statBg = isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC";
    const statDivider = isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0";

    const headCellSx = {
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase" as const,
        color: tokens.textSecondary,
        py: 1.25,
        px: 1.75,
        borderBottom: `1px solid ${tableHeaderBorder}`,
        backgroundColor: tableHeaderBg,
        whiteSpace: "nowrap" as const,
    };

    const bodyCellSx = {
        verticalAlign: "middle" as const,
        py: 1.5,
        px: 1.75,
        borderBottom: `1px solid ${tableRowBorder}`,
        fontSize: "0.83rem",
        color: tokens.textPrimary,
    };

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{ sx: paperSx(isMobile) }}
            onClick={(event) => event.stopPropagation()}
            aria-describedby="late-fee-summary-dialog"
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                <Box sx={{ ...headerSx, px: modalContentPx }}>
                    <Box sx={iconBoxSx("#f59e0b")}>
                        <CurrencyExchangeOutlined sx={{ fontSize: "1.1rem", color: "#f59e0b" }} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography
                            fontWeight={700}
                            sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
                        >
                            Late Fee Summary
                        </Typography>
                        <Typography
                            id="late-fee-summary-dialog"
                            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
                        >
                            {selectedInvoice?.mfgInvoiceNumber ? `Invoice: ${selectedInvoice.mfgInvoiceNumber} - ` : ""}
                            Late fee update history
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        ...bodySx,
                        p: 0,
                        px: modalContentPx,
                        py: 2.4,
                        mt: 0.5,
                        minHeight: 0,
                        width: "100%",
                        boxSizing: "border-box",
                        overflowY: "auto",
                        overflowX: "hidden",
                        "&::-webkit-scrollbar": { width: 6, height: 6 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                            borderRadius: 4,
                        },
                    }}
                >
                    {lateFeeHistory.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                mb: 2.5,
                                borderRadius: "10px",
                                border: `1px solid ${tokens.border}`,
                                backgroundColor: statBg,
                                overflow: "hidden",
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2,
                                    px: 2.2,
                                    py: 1.6,
                                    borderRight: { xs: "none", sm: `1px solid ${statDivider}` },
                                    borderBottom: { xs: `1px solid ${statDivider}`, sm: "none" },
                                }}
                            >
                                <CurrencyExchangeOutlined sx={{ fontSize: "1.1rem", color: "#f59e0b", flexShrink: 0 }} />
                                <Box>
                                    <Typography sx={{ fontSize: "0.7rem", color: tokens.textSecondary, fontWeight: 600, lineHeight: 1 }}>
                                        Total Late Fees
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#f59e0b", mt: 0.3, lineHeight: 1 }}>
                                        {formatCurrency(selectedInvoice?.lateFee ?? 0)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2,
                                    px: 2.2,
                                    py: 1.6,
                                }}
                            >
                                <CalendarMonthOutlined sx={{ fontSize: "1.1rem", color: "#ef4444", flexShrink: 0 }} />
                                <Box>
                                    <Typography sx={{ fontSize: "0.7rem", color: tokens.textSecondary, fontWeight: 600, lineHeight: 1 }}>
                                        Overdue Days
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#ef4444", mt: 0.3, lineHeight: 1 }}>
                                        {formatOverdueDays(selectedInvoice?.invoiceLateDays)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            borderRadius: "12px",
                            border: `1px solid ${tokens.border}`,
                            overflowX: "auto",
                            overflowY: "visible",
                            backgroundColor: tableRowBg,
                            boxShadow: "none",
                            "&::-webkit-scrollbar": { height: 6, width: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Table
                            stickyHeader
                            size="small"
                            sx={{
                                width: "100%",
                                tableLayout: "fixed",
                                minWidth: isMobile ? 620 : 0,
                            }}
                            aria-label="late fee summary table"
                        >
                            <colgroup>
                                <col style={{ width: "14%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "48%" }} />
                                <col style={{ width: "20%" }} />
                            </colgroup>
                            <TableHead>
                                <TableRow sx={{ "& th": { backgroundColor: tableHeaderBg } }}>
                                    <TableCell sx={headCellSx}>Type</TableCell>
                                    <TableCell sx={headCellSx}>Date</TableCell>
                                    <TableCell sx={headCellSx}>Action Message</TableCell>
                                    <TableCell sx={headCellSx}>Updated By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lateFeeHistory.length > 0 ? (
                                    lateFeeHistory.map((row, index) => {
                                        const typeSx = getTypeChipSx(row.type);
                                        return (
                                            <TableRow
                                                key={row.auditLogId ?? index}
                                                hover
                                                sx={{
                                                    backgroundColor: tableRowBg,
                                                    "&:nth-of-type(even)": {
                                                        backgroundColor: isDark
                                                            ? "rgba(255,255,255,0.025)"
                                                            : tokens.cardBg,
                                                    },
                                                    "&:hover": { backgroundColor: tableRowHover },
                                                    "&:last-child td": { borderBottom: "none" },
                                                }}
                                            >
                                                <TableCell sx={bodyCellSx}>
                                                    <Chip
                                                        label={row.type || "-"}
                                                        size="small"
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            fontWeight: 800,
                                                            height: 24,
                                                            color: typeSx.color,
                                                            backgroundColor: typeSx.backgroundColor,
                                                            border: `1px solid ${typeSx.borderColor}`,
                                                            "& .MuiChip-label": { px: 1 },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 500, color: tokens.textPrimary }}>
                                                        {formatDateTime(row.auditTimestamp)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "0.83rem",
                                                            fontWeight: 500,
                                                            color: tokens.textPrimary,
                                                            lineHeight: 1.4,
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {row.message || "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "0.83rem",
                                                            fontWeight: 500,
                                                            color: row.updatedBy ? tokens.textPrimary : tokens.textSecondary,
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {row.updatedBy || "System"}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            sx={{
                                                textAlign: "center",
                                                py: 5,
                                                backgroundColor: tableEmptyBg,
                                                borderBottom: "none",
                                            }}
                                        >
                                            <CurrencyExchangeOutlined
                                                sx={{
                                                    fontSize: "2.5rem",
                                                    color: tokens.textSecondary,
                                                    opacity: 0.4,
                                                    display: "block",
                                                    mx: "auto",
                                                    mb: 1,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: "0.88rem",
                                                    fontWeight: 500,
                                                    color: tokens.textSecondary,
                                                }}
                                            >
                                                No late fee history available
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box sx={{ ...footerSx, px: modalContentPx }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", flexDirection: { xs: "column", sm: "row" } }}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                            onClick={onClose}
                            sx={cancelBtnSx}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default LateFeeSummaryPopup;
